import { useEffect } from 'react';
import type { ConstructorValue } from './types';
import { ConstructorProvider, useConstructor } from './store/useConstructor';
import { applyConstructorOverlay, clearConstructor } from './apply';
import { OverridesEditor } from './OverridesEditor';
import { ConstructorTemplateContext } from './store/ConstructorTemplate';
import type { ComponentTemplate, Scene } from './schema/components';

export interface DesignConstructorProps {
  initialValue?: ConstructorValue;
}

export interface DesignConstructorBodyProps {
  /** When set, OverridesEditor filters its list to this template + shared. */
  template?: ComponentTemplate;
  /**
   * Schema `name` of the component the operator just clicked in the child
   * preview iframe. When this prop transitions to a non-null value the
   * body forwards the name down to `OverridesEditor` → `SectionEditor` for
   * scroll / highlight.
   *
   * Null = nothing selected yet (initial state).
   */
  selectedComponent?: string | null;
  /** DS variant name the operator clicked, if the component renders
   *  per-variant (e.g. Button → 'primary' | 'accent-gloss'). When set,
   *  ComponentSection auto-switches its local variant picker to it so
   *  the operator sees the knobs of the kind of element they clicked. */
  selectedVariant?: string;
  /** Monotonically-increasing tick — bumped on every selection so re-clicking
   *  the SAME component triggers a fresh scroll / highlight. */
  selectionNonce?: number;
  /** Active scene the preview iframe is on. SectionEditor uses this to
   *  filter the visible component list down to entries whose `scenes`
   *  include it (or that have no `scenes` field at all — cross-cutting). */
  currentScene?: Scene;
}

/**
 * DesignConstructor — top-level shell. Provides Constructor context and
 * applies palette changes to :root via useEffect. Copy/Paste/Reset live in
 * ConstructorTopBar, not here.
 */
export function DesignConstructor({ initialValue }: DesignConstructorProps) {
  return (
    <ConstructorProvider {...(initialValue ? { initialValue } : {})}>
      <DesignConstructorBody />
    </ConstructorProvider>
  );
}

export function DesignConstructorBody({
  template,
  selectedComponent = null,
  selectedVariant,
  selectionNonce,
  currentScene,
}: DesignConstructorBodyProps = {}) {
  const { state } = useConstructor();

  // Apply on every state change by *merging* the operator overlay onto
  // the backend baseline — never the raw overlay alone. Using
  // `applyConstructor(state.value)` here used to wipe baseline vars that
  // weren't part of the overlay (because applyConstructor's `writtenCoVars`
  // diff removes any var not in the new value), and the cleanup also
  // called `clearConstructor()` which reset `backendBaseline` to empty.
  // After the first knob change Copy JSON only saw the overlay.
  // applyConstructorOverlay does merge(baseline, overlay) → all baseline
  // vars stay, the operator's tweak layers on top.
  useEffect(() => {
    applyConstructorOverlay(state.value);
  }, [state.value]);

  // Permanent cleanup only — runs once when the constructor unmounts (e.g.
  // navigating away from /design-system). Keeping it inside the effect
  // above ran it on every state change and obliterated backendBaseline.
  useEffect(() => () => clearConstructor(), []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-3">
          <ConstructorTemplateContext value={template}>
            <OverridesEditor
              {...(template ? { template } : {})}
              selectedComponent={selectedComponent}
              {...(selectedVariant ? { selectedVariant } : {})}
              {...(selectionNonce !== undefined ? { selectionNonce } : {})}
              {...(currentScene ? { currentScene } : {})}
            />
          </ConstructorTemplateContext>
        </div>
      </div>
    </div>
  );
}
